import Tone from 'tone';
import {uuid} from 'uuidv4';
import {lfoFactory} from './LFOFactory.js';
import {chainFactory} from './LFOChainFactory.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import {A_RATE} from 'constants/automation-rates.js';
import {flip} from 'genetix/Flip.js';
import RateParameter from 'parameters/RateParameter.js';
import Scaler from './Scaler.js';
import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';
import {AbstractAudioParamParameter} from 'parameters/AbstractAudioParamParameter.js';
import CalculatedLFO from './CalculatedLFO.js';
import {isNumber} from 'utils/IsNumber.js';
import Modulator from './Modulator.js';
import {setEffectParameterValues} from 'actions/index.js';

function getLFO() {
    return lfoFactory.useLFO();
;
}

class LFO extends CalculatedLFO(Modulator) {
    constructor(sequencerEngine, mixSession, context, number) {
        super(sequencerEngine, mixSession, context, number);

        this.id = uuid();
        this.lastValue = undefined;
        this.lastStep = undefined;
        this.number = number;
        this.rawLFORate = undefined;
        this.mixSession = mixSession;
        this.lfo = getLFO();
        this.setupLFOParameters();
        this.isSynced = true;
        this.state = "stopped";

        sequencerEngine.dispatch(
            setEffectParameterValues(this.initialBulkValues));
    }

    getType() {
        return 'LFO';
    }

    dispose() {
        if (!this.lfo) {
            // already disposed
            return;
        }
        lfoFactory.releaseLFO(this.lfo);

        for (let i in this.parameterIdToVoiceToNode) {
            let voiceToChain = this.parameterIdToVoiceToNode[i];
            for (let voice in voiceToChain) {
                let chain = voiceToChain[voice];
                let toDispose = chain.slice(0, chain.length - 1)
                    .filter(s => s.dispose && s.input);
                toDispose
                    .forEach(s => {
                        s.dispose();
                    });
            }
        }
        this.parameterIdToVoiceToNode = undefined;
        this.lfo = undefined;
        this.lfoRateParameter = undefined;
        this.lfoMinParameter = undefined;
        this.lfoMaxParameter = undefined;
        this.lfoPhaseParameter = undefined;
        this.lfoAmpParameter = undefined;
    }

    getDepthFromChain(chain) {
        return chain[2];
    }

    getScaleFromChain(chain) {
        // the scaler is the 3rd node in the chain
        return chain[1];
    }

    set startTime(startTime) {
        this._startTime = startTime;
    }

    get startTime() {
        return this._startTime;
    }

    start(time) {
        this.stop();
        if (Tone.Master.context.isOffline) {
            if (time !== undefined) {
                this.lfo.start(Tone.Master.context._currentTime + time + 0.00001);
            } else {
                this.lfo.start(Tone.Master.context._currentTime + 0.00001);
            }
        } else {
            this.lfo.start(Tone.Master.context.isOffline ? Tone.Master.context._currentTime + time : time);
        }
        this.state = "started";
        this.lfo.lfoType = this.lfoType;
        this.startTime = time || Tone.Master.context.currentTime;
        this.stoppedTime = undefined;
    }

    stop() {
        this.lfo.stop();
        this.state = "stopped";
        this.stoppedTime = Tone.Master.context.isOffline ?
            Tone.Master.context._currentTime : Tone.Master.context.currentTime;
    }

    getModulator() {
        return this.lfo;
    }

    getChain(parameter, audioNode) {
        return [... chainFactory.useChain(parameter, this.sequencerEngine), audioNode];
        /*
        let normalizer = new Tone.Normalize(-100, 100);
        // scaler takes the parameter min/max to scale the lfo
        // to this range (this range can later be modded via the mod matrix)
        let scaler = new Tone.Scale(parameter.min, parameter.max);
        scaler.min = parameter.min;
        scaler.max = parameter.max;
        let doRamp = !Tone.Master.context.isOffline;
        let depth = new Tone.Gain(1);
        return [normalizer, scaler, depth, audioNode];
        */
    }

    releaseChain(chain) {
        chainFactory.releaseChain(chain.slice(0, chain.length - 1), this.sequencerEngine);
    }

    // each voice of the synth gets its signal (tied to its parameter)
    // connected to the lfo via this function
    connect(signal, parameter, voice) {
        if (signal === undefined) {
            let scaler = new Scaler(parameter.min, parameter.max);
            this.parameterToScaler[parameter.id] = scaler;
            return;
        }
        let input = signal;
        if (signal.input && !Array.isArray(signal.input)) {
            input = signal.input;
        }
        let chain = super.connect(input, parameter, voice);
        if (this.state === "stopped") {
            this.start();
        }
    }

    setupLFOParameters() {
        this.lfoTypeParameter = new AbstractParameter(
            'LFO Type',
            this, (o, x) => {
                this.stop();
                this.lfo.type = x || 'sine';
                this.start();
            },
            {
                min: 0,
                max: 1,
                defaultValue: 'sine',
                automatable: false
            });
        
        this.registerParameter(this.lfoTypeParameter);

        let number = this.number;
        this.lfoRateParameter = new RateParameter(
           'LFO Rate',
            this.lfo.frequency, (o, x) => {
                if (this.lfo.frequency.input) {
                    this.lfo.frequency.value = new Tone.Frequency(x);
                }
            },
            {
                min:0,
                max: 800,
                unit: '',
                defaultValue: Math.pow(2, (5-this.number)) + 'n',
                });
        

        this.registerParameter(this.lfoRateParameter);

        this.lfoMinParameter = new AbstractAudioParamParameter(
            'LFO Min',
            this.lfo._scaler._add, (o, x) => {
                if (this.lfo._scaler._add.input) {
                    this.lfo.min = x;
                }
            },
            {
                min:-100,
                max: 100,
                unit: '',
                defaultValue: -100,
                });
        
        
        this.registerParameter(this.lfoMinParameter);

        this.lfoMaxParameter = new AbstractAudioParamParameter(
            'LFO Max',
            this.lfo._scaler._scale, (o, x) => {
                if (this.lfo._scaler._scale.input) {
                    this.lfo.max = x;
                }
            },
            {
                min:-100,
                max: 100,
                unit: '',
                defaultValue: 100,
                });
        
        this.registerParameter(this.lfoMaxParameter);

        this.lfoAmpParameter = new AbstractAudioParamParameter(
            'LFO Amplitude',
            this.lfo.amplitude, (o, x) => {
                if (this.lfo.amplitude.input) {
                    this.lfo.amplitude.value = x;
                }
            },
            {
                min:0,
                max: 1,
                unit: '',
                defaultValue: 1,
                automationRate: A_RATE,
                });
        
        this.registerParameter(this.lfoAmpParameter);

        this.lfoPhaseParameter = new AbstractParameter(
            'LFO Phase',
            this, (o, x) => {
                if (this.lfo.frequency.input) {
                    this.lfo.phase = x;
                }
            },
            {
                min:0,
                max: 360,
                unit: '',
                defaultValue: 0,
            });
        
        
        this.registerParameter(this.lfoPhaseParameter);
    }

    registerParameter(parameter) {
        this.initialBulkValues = {
            ... this.initialBulkValues,
            ... parameter.setStore(this.store)
        };
        parameter.setMixSession(this.mixSession);
        parameter.setContext(this.context);
        this.parameters.push(parameter);
    }

    get name() {
        return "LFO " + this.number;
    }

    get lfoType() {
        if (this.lfoTypeParameter === undefined) {
            return 'sine';
        }
        return this.lfoTypeParameter.get();
    }

    set lfoType(x) {
        this.lfoTypeParameter.set(x, false);
    }

    get lfoRate() {
        return this.lfoRateParameter.get(true);
    }

    set lfoRate(x) {
        this.lfoRateParameter.set(x, false);
        this.rawLFORate = x;
    }

    get lfoMin() {
        return this.lfoMinParameter.get();
    }

    set lfoMin(x) {
        this.lfoMinParameter.set(x);
    }

    parameterHasCycle(parameter, origin, lfo, counter=0) {
        if (counter > 6) {
            return true;
        }

        if (!origin) {
            origin = parameter;
        } else {
            if (parameter.lfos.includes(this) || parameter.lfos.includes(lfo)) {
                return true;
            }
        }
        // checks whether parameter has a cycle (and thus should
        // be just directly checked for to avoid an infinite loop)
        for (let lfo of parameter.lfos) {
            if (lfo.parameters.
                some(p => this.parameterHasCycle(p, origin, lfo, counter++))) {
                return true;
            }
        }
        return false;
    }

    get lfoMax() {
        return this.lfoMaxParameter.get();
    }

    set lfoMax(x) {
        this.lfoMaxParameter.set(x);
    }

    get lfoPhase() {
        return this.lfoPhaseParameter.get();
    }

    set lfoPhase(x) {
        this.lfoPhaseParameter.set(x);
    }

    onBPMUpdated() {
        let lfoRate = this.lfoRateParameter.get(true);
        this.lfoRateParameter.set(lfoRate, true, true);
    }

    getJson(isPreset) {
        // TODO: figure out whats going on with lfo rate and why parameter locks dont work with it
        let rate = this.lfoRate;
        if (typeof rate !== 'number') {
            if (this.rawLFORate !== undefined) {
                rate = this.rawLFORate;
            }

        }
        return {
            id: this.id,
            lfoRateParameter: this.lfoRateParameter.getJson(isPreset),
            lfoRate: rate,
            lfoMinParameter: this.lfoMinParameter.getJson(isPreset),
            lfoMaxParameter: this.lfoMaxParameter.getJson(isPreset),
            lfoTypeParameter: this.lfoTypeParameter.getJson(isPreset),
            lfoAmpParameter: this.lfoAmpParameter.getJson(isPreset),
            lfoPhaseParameter: this.lfoPhaseParameter.getJson(isPreset),
            isSynced: this.isSynced,
        };
    };

    fromJson(json, ignoreId) {
        // first disconnect all modulators
        let wasStarted = this.lfo.state === 'started';
        if (wasStarted) {
            this.stop();
        }
        if (!ignoreId && json.id) {
            this.id = json.id;
        }
        let bulk = {
            ... loadParameterFromJson(
                json, 'lfoRateParameter', this.lfoRateParameter, this.sequencerEngine, ignoreId)
        };
        if ('isSynced' in json) {
            this.isSynced = json.isSynced;
        }

        if ('lfoRate' in json) {
            if (isNumber(json.lfoRate)) {
                if (this.isSynced) {
                    bulkUpdate(
                        this.lfoRateParameter,
                        new Tone.Time(1 / json.lfoRate).toNotation(),
                        bulk);
                } else {
                    bulkUpdate(this.lfoRateParameter, json.lfoRate, bulk);
                }
            } else {
                bulkUpdate(this.lfoRateParameter, json.lfoRate, bulk);
            }
        }

        if ('lfoMinParameter' in json) {
            bulk = {
                ... bulk,
                ... loadParameterFromJson(
                    json, 'lfoMinParameter', this.lfoMinParameter, this.sequencerEngine, ignoreId)
            };
        }
        if ('lfoMin' in json) {
            bulkUpdate(this.lfoMinParameter, json.lfoMin, bulk);
        }

        if ('lfoMaxParameter' in json) {
            bulk = {
                ... bulk,
                ... loadParameterFromJson(
                    json, 'lfoMaxParameter', this.lfoMaxParameter, this.sequencerEngine, ignoreId)
            };
        }
        if ('lfoMax' in json) {
            bulkUpdate(this.lfoMaxParameter, json.lfoMax, bulk);
        }

        bulk = {
            ... bulk,
            ... loadParameterFromJson(
                json, 'lfoTypeParameter', this.lfoTypeParameter, this.sequencerEngine, ignoreId),
            ... loadParameterFromJson(
                json, 'lfoAmpParameter', this.lfoAmpParameter, this.sequencerEngine, ignoreId)
        };

        if ('lfoType' in json) {
            bulkUpdate(this.lfoTypeParameter, json.lfoType, bulk);
        }

        if ('lfoPhaseParameter' in json) {
            bulk = {
                ... bulk,
                ... loadParameterFromJson(
                    json, 'lfoPhaseParameter', this.lfoPhaseParameter, this.sequencerEngine, ignoreId)
            };
        }
        if ('lfoPhase' in json) {
            bulkUpdate(this.lfoPhaseParameter, json.lfoPhase, bulk);
        }

        if (wasStarted) {
            this.start();
        }
        return bulk;
    }
           
    copyFrom(lfo) {
        this.lfoType = lfo.lfoType;
        this.lfoRate = lfo.lfoRate;
        this.lfoMin = lfo.lfoMin;
        this.lfoMax = lfo.lfoMax;
        this.lfoPhase = lfo.lfoPhase;
        this.isSynced = lfo.isSynced;
    }

    mateJson(partner, probability=0.5) {
        if (flip(probability)) {
            this.lfoTypeParameter.disconnectFromAllModulators();
            this.lfoTypeParameter.fromJson(partner.lfoTypeParameter, this.sequencerEngine);
        }
        if (flip(probability)) {
            this.lfoRateParameter.disconnectFromAllModulators();
            this.lfoRate = partner.lfoRate;
            this.lfoRateParameter.fromJson(partner.lfoRateParameter, this.sequencerEngine);
        }
        if (flip(probability)) {
            this.lfoMinParameter.disconnectFromAllModulators();
            this.lfoMinParameter.fromJson(partner.lfoMinParameter, this.sequencerEngine);
        }
        if (flip(probability)) {
            this.lfoMaxParameter.disconnectFromAllModulators();
            this.lfoMaxParameter.fromJson(partner.lfoMaxParameter, this.sequencerEngine);
        }
        if (flip(probability)) {
            this.lfoPhaseParameter.disconnectFromAllModulators();
            this.lfoPhaseParameter.fromJson(partner.lfoPhaseParameter, this.sequencerEngine);
        }
    }

    getCopy(sequencerEngine=this.sequencerEngine) {
        let lfo = new LFO(sequencerEngine, this.mixSession, this.context, this.number);
        lfo.fromJson(this.getJson());
        return lfo;
    }
}

export default LFO;

